import React from 'react';
import { Box, Menu, MenuItem, Typography, useTheme, Fade } from '@mui/material';
import { Link } from 'react-router-dom';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';

export type BreadcrumbDropdownProps = {
    /** Titre affiché dans le breadcrumb */
    title: string;
    /** Dossiers siblings de la même commande */
    siblingDossiers: DossierOpenSearch[];
    /** ID du dossier courant */
    currentDossierId: number;
    /** Fonction pour générer la route vers un dossier */
    getDossierRoute: (id: number) => string;
    /** Si c'est l'élément actif du breadcrumb */
    isActive?: boolean;
};

const BreadcrumbDropdown: React.FunctionComponent<BreadcrumbDropdownProps> = function ({
    title,
    siblingDossiers,
    currentDossierId,
    getDossierRoute,
    isActive = false,
}) {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const textGris = theme.palette.text.gris50;
    const primaryText = theme.palette.text.primary;
    const hoverBg = theme.palette.grey[200];
    const activeBg = theme.palette.grey[200];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (siblingDossiers.length > 1) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const currentDossier = siblingDossiers.find((dossier) => dossier.id === currentDossierId);
    const commandeNumero = currentDossier?.collectionNumero;

    if (siblingDossiers.length <= 1) {
        return (
            <Box
                sx={{
                    padding: '6px 8px',
                    borderRadius: '6px',
                    backgroundColor: isActive ? activeBg : 'transparent',
                    '&:hover': {
                        backgroundColor: hoverBg,
                    },
                }}
            >
                <Typography
                    sx={{
                        color: textGris,
                        fontSize: '14px',
                        lineHeight: '24px',
                        fontWeight: 400,
                    }}
                >
                    {title}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    backgroundColor: isActive ? activeBg : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: hoverBg,
                        '& .breadcrumb-text': {
                            color: primaryText,
                        },
                    },
                }}
                onClick={handleClick}
                aria-controls={open ? 'dossier-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
            >
                <Typography
                    className="breadcrumb-text"
                    sx={{
                        color: textGris,
                        fontSize: '14px',
                        lineHeight: '24px',
                        fontWeight: 400,
                        transition: 'color 0.2s ease-in-out',
                    }}
                >
                    {title}
                </Typography>
                {/* Flèche dropdown : droite quand fermé, bas quand ouvert */}
                <FaOptionIcon
                    icon={['fasl', open ? 'chevron-down' : 'chevron-right']}
                    size="xs"
                    sx={{
                        color: textGris,
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'transform 0.2s ease-in-out',
                    }}
                />
            </Box>

            <Menu
                id="dossier-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 200 }}
                MenuListProps={{
                    'aria-labelledby': 'dossier-button',
                    sx: { py: 1 },
                }}
                sx={{
                    '& .MuiPaper-root': {
                        minWidth: 350,
                        maxWidth: 500,
                        boxShadow: `0px 4px 20px ${theme.palette.background.shadow}`,
                        borderRadius: '12px',
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {/* En-tête du menu */}
                <Box sx={{ px: 2, py: 1, mb: 0.5 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: textGris,
                            fontSize: '14px',
                            fontWeight: 500,
                        }}
                    >
                        Dossiers de la commande #{commandeNumero}
                    </Typography>
                </Box>

                {/* Liste des dossiers */}
                {siblingDossiers.map((dossier, index) => {
                    const isCurrent = dossier.id === currentDossierId;
                    const formalites = dossier.formalites || [];
                    const premiereFormalite = formalites[0];
                    const formalitesSupplementaires =
                        formalites.length > 1 ? formalites.length - 1 : 0;

                    // Affichage de la deuxième ligne
                    const secondaryText = premiereFormalite
                        ? `${premiereFormalite.libelle}${formalitesSupplementaires > 0 ? ` +${formalitesSupplementaires}` : ''}`
                        : dossier.statutLibelle || 'En cours';

                    return (
                        <MenuItem
                            key={dossier.id}
                            component={Link}
                            to={getDossierRoute(dossier.id!)}
                            onClick={handleClose}
                            sx={{
                                py: 1.5,
                                px: 2,
                                mx: 1.5,
                                mb: index < siblingDossiers.length - 1 ? 0.5 : 0,
                                borderRadius: '12px',
                                backgroundColor: isCurrent
                                    ? `${theme.palette.primary.main}15`
                                    : 'transparent',
                                '&:hover': {
                                    backgroundColor: isCurrent
                                        ? `${theme.palette.primary.main}25`
                                        : theme.palette.grey[100],
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    width: '100%',
                                }}
                            >
                                {/* Icône de dossier à gauche */}
                                <Box
                                    sx={{
                                        mt: 0.25,
                                        flexShrink: 0,
                                    }}
                                >
                                    <FaOptionIcon
                                        icon={['fasl', 'folder']}
                                        size="sm"
                                        color={primaryText}
                                    />
                                </Box>

                                {/* Contenu du dossier */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Première ligne : Nom + Ref */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 0.25,
                                        }}
                                    >
                                        {/* Nom du dossier */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: primaryText,
                                                fontSize: '15px',
                                                fontWeight: 500,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {dossier.societeDenomination || `Légale Société`}
                                        </Typography>
                                        {/* Référence en gris à côté */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: textGris,
                                                fontSize: '13px',
                                                lineHeight: 1.6,
                                                fontWeight: 500,
                                            }}
                                        >
                                            #{dossier.numero}
                                        </Typography>
                                    </Box>

                                    {/* Deuxième ligne : Formalité(s) ou statut */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: textGris,
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            lineHeight: 1.2,
                                            display: 'block',
                                        }}
                                    >
                                        {secondaryText}
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default BreadcrumbDropdown;
